/**
 * Deposit and Withdrawal History Service
 * Unified service for tracking deposits and withdrawals
 */

const API_URL = '';

/**
 * Fetch deposit history
 */
export const fetchDepositHistory = async (status = null, limit = 50) => {
  try {
    const token = localStorage.getItem('token');
    let url = `${API_URL}/api/history/deposits?limit=${limit}`;
    
    if (status) {
      url += `&status=${status}`;
    }
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (response.ok) {
      return await response.json();
    }
    throw new Error('Failed to fetch deposit history');
  } catch (error) {
    console.error('Error fetching deposit history:', error);
    throw error;
  }
};

/**
 * Fetch withdrawal history
 */
export const fetchWithdrawalHistory = async (status = null, limit = 50) => {
  try {
    const token = localStorage.getItem('token');
    let url = `${API_URL}/api/history/withdrawals?limit=${limit}`;
    
    if (status) {
      url += `&status=${status}`;
    }
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (response.ok) {
      return await response.json();
    }
    throw new Error('Failed to fetch withdrawal history');
  } catch (error) {
    console.error('Error fetching withdrawal history:', error);
    throw error;
  }
};

/**
 * Fetch combined deposit and withdrawal history
 */
export const fetchCombinedHistory = async (transactionType = 'all', status = null, limit = 50) => {
  try {
    const token = localStorage.getItem('token');
    let url = `${API_URL}/api/history/combined?transaction_type=${transactionType}&limit=${limit}`;
    
    if (status) {
      url += `&status=${status}`;
    }
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (response.ok) {
      return await response.json();
    }
    throw new Error('Failed to fetch combined history');
  } catch (error) {
    console.error('Error fetching combined history:', error);
    throw error;
  }
};

/**
 * Fetch deposit and withdrawal summary
 */
export const fetchSummary = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/api/history/summary`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (response.ok) {
      return await response.json();
    }
    throw new Error('Failed to fetch summary');
  } catch (error) {
    console.error('Error fetching summary:', error);
    throw error;
  }
};

/**
 * Get status badge color
 */
export const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed':
      return 'bg-green-500/20 text-green-400 border-green-500/30';
    case 'pending':
      return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    case 'rejected':
    case 'failed':
      return 'bg-red-500/20 text-red-400 border-red-500/30';
    default:
      return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  }
};

/**
 * Get type badge color
 */
export const getTypeColor = (type) => {
  switch (type?.toLowerCase()) {
    case 'deposit':
      return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
    case 'withdrawal':
      return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
    default:
      return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  }
};

/**
 * Format timestamp to readable date
 */
export const formatDate = (timestamp) => {
  if (!timestamp) return 'N/A';
  const date = new Date(timestamp);
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    return 'N/A';
  }
  
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Format amount with currency
 */
export const formatAmount = (amount, currency) => {
  if (typeof amount !== 'number') return '0';
  
  const decimals = ['BTC', 'ETH'].includes(currency?.toUpperCase()) ? 8 : 2;
  
  return amount.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

/**
 * Get status icon
 */
export const getStatusIcon = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed':
      return '✓';
    case 'pending':
      return '⏳';
    case 'rejected':
    case 'failed':
      return '✗';
    default:
      return '•';
  }
};
